// Re-checks a case roll in the browser, with no help from our server beyond the
// proof data. Mirrors rollFloat/pickFromTable in src/cases.js exactly — if one
// changes, the other has to.
const enc = new TextEncoder();
const toHex = (buf) => [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');

export async function sha256Hex(text) {
  return toHex(await crypto.subtle.digest('SHA-256', enc.encode(text)));
}

/** HMAC-SHA256(serverSeed, `${clientSeed}:${nonce}`), first 48 bits as a fraction in [0, 1). */
export async function rollFloat(serverSeed, clientSeed, nonce) {
  const key = await crypto.subtle.importKey('raw', enc.encode(serverSeed), { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  const mac = new Uint8Array(await crypto.subtle.sign('HMAC', key, enc.encode(`${clientSeed}:${nonce}`)));
  let n = 0;
  for (let i = 0; i < 6; i++) n = n * 256 + mac[i];
  return n / 2 ** 48;
}

/** `table` is [[dropId, name, weight], ...] in drop-id order. */
export function pickFromTable(table, roll) {
  const total = table.reduce((sum, row) => sum + row[2], 0);
  const target = roll * total;
  let acc = 0;
  for (const row of table) {
    acc += row[2];
    if (target < acc) return row;
  }
  return table[table.length - 1];
}

/** Runs every check on a proof from /api/cases/pulls/:id/proof. Returns
 *  `{ state: 'pending' | 'legacy' | 'verified' | 'failed', checks: [{ label, ok }] }`. */
export async function verifyProof(proof) {
  if (proof.legacy) return { state: 'legacy', checks: [] };
  if (!proof.serverSeed) return { state: 'pending', checks: [] };

  const checks = [];
  const hash = await sha256Hex(proof.serverSeed);
  checks.push({ label: 'Server seed matches the hash shown before you opened', ok: hash === proof.serverSeedHash });

  const roll = await rollFloat(proof.serverSeed, proof.clientSeed, proof.nonce);
  checks.push({ label: `Recomputed roll ${roll.toFixed(8)} matches the recorded roll`, ok: roll === proof.roll });

  if (proof.table) {
    const picked = pickFromTable(proof.table, roll);
    checks.push({ label: `Roll lands on "${picked[1]}", the item you got`, ok: picked[0] === proof.dropId });
  } else {
    checks.push({ label: 'Drop table for this open is on record', ok: false });
  }
  return { state: checks.every((c) => c.ok) ? 'verified' : 'failed', checks };
}
